/**
 * FISHER CALCULATOR
 * 
 * Implementação da equação de Fisher para conversão entre taxas reais e nominais
 * Resolve P0.2: Garantir consistência entre WACC (nominal) e g (real/nominal)
 * 
 * Equação de Fisher (Irving Fisher, 1930):
 * (1 + nominal) = (1 + real) × (1 + inflação)
 * 
 * Aplicação em Valuation:
 * - WACC sempre nominal (inclui inflação nas expectativas de mercado)
 * - g (crescimento perpétuo) pode ser real ou nominal
 * - Fórmula de Gordon requer ambos na mesma base
 * 
 * Impacto de erro: ±20% no EV
 * 
 * @module utils/fisherCalculator
 */

import type { RateMode } from '../types/valuation';

// ============================================================================
// INTERFACES
// ============================================================================

export interface FisherParams {
  /** Taxa real (ajustada pela inflação) */
  real: number;
  
  /** Taxa de inflação */
  inflation: number;
}

export interface FisherResult {
  /** Taxa real (input ou calculada) */
  real: number;
  
  /** Taxa nominal (calculada ou input) */
  nominal: number;
  
  /** Inflação usada */
  inflation: number;
  
  /** Fórmula em texto para auditoria */
  formula: string;
}

export interface WACCvsGValidation {
  /** Se a validação passou */
  isValid: boolean;
  
  /** Warnings encontrados */
  warnings: string[];
  
  /** Erros críticos */
  errors: string[];
  
  /** Sugestões de correção */
  suggestions: string[];
}

// ============================================================================
// CONVERSÕES FISHER
// ============================================================================

/**
 * Converte taxa real para nominal usando Fisher equation
 * 
 * Fórmula: nominal = (1 + real) × (1 + inflação) - 1
 * 
 * @param real - Taxa real (ex: 0.03 = 3%)
 * @param inflation - Taxa de inflação (ex: 0.045 = 4.5%)
 * @returns Taxa nominal
 * 
 * @example
 * ```typescript
 * const g_nominal = realToNominal(0.03, 0.045);
 * console.log(g_nominal); // 0.07635 (7.635%)
 * ```
 */
export function realToNominal(real: number, inflation: number): number {
  return (1 + real) * (1 + inflation) - 1;
}

/**
 * Converte taxa nominal para real usando Fisher equation inversa
 * 
 * Fórmula: real = [(1 + nominal) / (1 + inflação)] - 1
 * 
 * @param nominal - Taxa nominal (ex: 0.07635 = 7.635%)
 * @param inflation - Taxa de inflação (ex: 0.045 = 4.5%)
 * @returns Taxa real
 * 
 * @example
 * ```typescript
 * const g_real = nominalToReal(0.07635, 0.045);
 * console.log(g_real); // 0.03 (3%)
 * ```
 */
export function nominalToReal(nominal: number, inflation: number): number {
  return ((1 + nominal) / (1 + inflation)) - 1;
}

/**
 * Aplica Fisher equation com resultados detalhados
 * 
 * @param params - Parâmetros (real + inflation)
 * @returns Resultado completo com fórmula
 * 
 * @example
 * ```typescript
 * const result = applyFisher({ real: 0.03, inflation: 0.045 });
 * console.log(result.nominal); // 0.07635
 * console.log(result.formula); // "(1 + 3.00%) × (1 + 4.50%) - 1 = 7.64%"
 * ```
 */
export function applyFisher(params: FisherParams): FisherResult {
  const nominal = realToNominal(params.real, params.inflation);
  
  return {
    real: params.real,
    nominal,
    inflation: params.inflation,
    formula: `(1 + ${formatPercent(params.real)}) × (1 + ${formatPercent(params.inflation)}) - 1 = ${formatPercent(nominal)}`,
  };
}

/**
 * Garante que uma taxa esteja no modo correto
 * 
 * @param value - Valor da taxa
 * @param currentMode - Modo atual da taxa
 * @param targetMode - Modo desejado
 * @param inflation - Inflação para conversão
 * @returns Taxa convertida para o modo target
 * 
 * @example
 * ```typescript
 * const g_nominal = ensureMode(0.03, 'real', 'nominal', 0.045);
 * console.log(g_nominal); // 0.07635
 * ```
 */
export function ensureMode(
  value: number,
  currentMode: RateMode,
  targetMode: RateMode,
  inflation: number
): number {
  if (currentMode === targetMode) {
    return value;
  }
  
  if (targetMode === 'nominal') {
    // Converter real → nominal
    return realToNominal(value, inflation);
  } else {
    // Converter nominal → real
    return nominalToReal(value, inflation);
  }
}

// ============================================================================
// VALIDAÇÕES
// ============================================================================

/**
 * Valida consistência entre WACC e g para fórmula de Gordon
 * 
 * Validações:
 * 1. WACC > g (obrigatório, senão VT = ∞)
 * 2. WACC e g na mesma base (nominal/nominal ou real/real)
 * 3. Spread WACC - g razoável (mínimo 1-2%)
 * 
 * @param wacc - WACC (Cost of Capital)
 * @param g - Taxa de crescimento perpétuo
 * @param waccMode - Modo do WACC ('nominal' sempre)
 * @param gMode - Modo do g ('real' ou 'nominal')
 * @returns Resultado da validação
 * 
 * @example
 * ```typescript
 * const validation = validateWACCvsG(0.145, 0.03, 'nominal', 'real');
 * if (!validation.isValid) {
 *   console.error(validation.errors);
 * }
 * ```
 */
export function validateWACCvsG(
  wacc: number,
  g: number,
  waccMode: string,
  gMode: string
): WACCvsGValidation {
  const warnings: string[] = [];
  const errors: string[] = [];
  const suggestions: string[] = [];

  // Validação 1: WACC > g (crítico)
  if (wacc <= g) {
    errors.push(
      `❌ CRÍTICO: WACC (${formatPercent(wacc)}) <= g (${formatPercent(g)}). ` +
      `Fórmula de Gordon inválida (Terminal Value = ∞).`
    );
    suggestions.push(
      `WACC deve ser sempre maior que g. ` +
      `Ajuste: reduzir g ou aumentar WACC.`
    );
  }

  // Validação 2: Bases consistentes (crítico)
  if (waccMode !== gMode) {
    errors.push(
      `❌ CRÍTICO: WACC (${waccMode}) e g (${gMode}) em bases diferentes! ` +
      `Fórmula de Gordon requer ambos na mesma base.`
    );
    suggestions.push(
      `Converter g para ${waccMode} usando Fisher equation. ` +
      `WACC é sempre nominal, então g também deve ser nominal.`
    );
  }

  // Validação 3: Spread razoável (warning)
  const spread = wacc - g;
  if (spread < 0.01 && wacc > g) {
    // Spread < 1%
    warnings.push(
      `⚠️ Spread WACC-g muito baixo: ${formatPercent(spread)} (${(spread * 100).toFixed(2)}%). ` +
      `Terminal Value será muito alto. Spread típico: 5-10%.`
    );
    suggestions.push(
      `Revisar premissas: g perpétuo muito otimista ou WACC muito baixo.`
    );
  } else if (spread > 0.15) {
    // Spread > 15%
    warnings.push(
      `⚠️ Spread WACC-g muito alto: ${formatPercent(spread)} (${(spread * 100).toFixed(2)}%). ` +
      `Terminal Value será muito baixo. Spread típico: 5-10%.`
    );
    suggestions.push(
      `Revisar premissas: g perpétuo muito conservador ou WACC muito alto.`
    );
  }

  const isValid = errors.length === 0;

  return {
    isValid,
    warnings,
    errors,
    suggestions,
  };
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Formata número como percentual para exibição
 * 
 * @param value - Valor decimal (ex: 0.03)
 * @param decimals - Casas decimais (padrão: 2)
 * @returns String formatada (ex: "3.00%")
 */
export function formatPercent(value: number, decimals: number = 2): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Calcula Terminal Value usando fórmula de Gordon
 * 
 * VT = FCFF × (1 + g) / (WACC - g)
 * 
 * @param fcff - Free Cash Flow to Firm do último ano projetado
 * @param wacc - WACC (nominal)
 * @param g - Taxa de crescimento perpétuo (nominal)
 * @returns Terminal Value
 * 
 * @throws {Error} Se WACC <= g
 */
export function calculateTerminalValue(
  fcff: number,
  wacc: number,
  g: number
): number {
  if (wacc <= g) {
    throw new Error(
      `Terminal Value inválido: WACC (${formatPercent(wacc)}) <= g (${formatPercent(g)})`
    );
  }

  const fcff_next = fcff * (1 + g);
  return fcff_next / (wacc - g);
}
